import { useState, useEffect, useRef } from "react";

import { useRestClientContext } from "../contexts/RestClientContext";
import { useTeamContext } from "../contexts/TeamContext";
import { Accessor, PropertyAttribute, PropertyFeature } from "../types/types";
import { Property } from "../rest/responses/Property";

export interface BatchedPropertiesCollection {
	progress: number;
	isLoading: boolean;
	type: String;
	features: PropertyFeature[];
	error?: string;
}

const PAGE_SIZE = 1_000;

function propertyToFeature(property: Property): PropertyFeature | null {
	const longitude = Number(property["location.lng"]);
	const latitude = Number(property["location.lat"]);

	if (isNaN(longitude) || isNaN(latitude)) {
		console.warn(`Property ${property.id} has invalid geometry and will be ignored`);
		return null;
	}

	const insulationPerformance = property["image_meta.insulation_performance"];

	return {
		type: "Feature",
		properties: {
			data: property,
			"metrics.epc_rating": Number(property["metrics.epc_rating"]),
			"image_meta.insulation_performance": insulationPerformance != null ? Number(insulationPerformance) : 0,
		},
		geometry: {
			type: "Point",
			coordinates: [longitude, latitude],
		},
	};
}

export const mapPropertyAttributes: PropertyAttribute[] = ["uprn"];
export const mapAccessors: Accessor[] = [
	"location.lat",
	"location.lng",
	"location.addr_no",
	"location.addr_street",
	"location.addr_city",
	"location.postcode",
	"metrics.epc_rating",
	"metrics.epc_rating_grade",
	"data_integrity.value",
	"image_meta.insulation_performance",
	"image_meta.image_urls",
];

export default function useBatchedProperties() {
	const client = useRestClientContext();
	const team = useTeamContext();
	const abortControllerRef = useRef<AbortController>();

	const [page, setPage] = useState(1);
	const [properties, setProperties] = useState<BatchedPropertiesCollection>({
		isLoading: true,
		type: "FeatureCollection",
		features: [],
		progress: 0,
	});

	useEffect(() => {
		setPage(1);
		setProperties({
			isLoading: true,
			type: "FeatureCollection",
			features: [],
			progress: 0,
		});
	}, [team.id]);

	useEffect(() => {
		if (abortControllerRef.current) abortControllerRef.current.abort();

		abortControllerRef.current = new AbortController();
		const signal = abortControllerRef.current.signal;

		client.v3
			.getProperties(
				team,
				mapAccessors,
				mapPropertyAttributes,
				undefined,
				undefined,
				{ pageIndex: page - 1, pageSize: PAGE_SIZE },
				undefined,
				signal
			)
			.then((json) => {
				const append: PropertyFeature[] = [];

				json.entries.forEach((property) => {
					const feature = propertyToFeature(property);
					if (feature) append.push(feature);
				});

				setProperties((prev) => ({
					isLoading: json.page_number < json.total_pages,
					progress: json.page_number / json.total_pages,
					type: "FeatureCollection",
					features: [...prev.features, ...append],
				}));

				if (json.page_number < json.total_pages) setPage((currentPage) => currentPage + 1);
			})
			.catch((e: Error) => {
				if (e.name === "AbortError") return;

				setProperties((prev) => ({
					...prev,
					error: e.message,
					isLoading: false,
				}));
				console.error("Error while fetching property batch", e);
			});

		return () => abortControllerRef.current?.abort();

		// NB: We ignore exhaustive deps here because it wants properties.features, but this is appended to constantly. This causes the last page to be fetched repeatedly
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [client, team.id, page]);

	return properties;
}
